import type { RichTextImageUploadRequestRequest } from '@ben/api/gen/codegen.schemas';
import { useApiV10MediaRichTextImagesCreate } from '@ben/api/gen/codegen';
import type { AxiosError } from 'axios';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { uploadToS3 } from './upload-to-s3';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

const generateFilename = (file: File): string => {
  const extension = file.name.split('.').pop() || 'png';
  return `${crypto.randomUUID()}.${extension}`;
};

interface UploadRichTextImageOptions {
  employerId?: number;
}

export const useUploadRichTextImage = (
  options: UploadRichTextImageOptions = {}
) => {
  const { employerId } = options;
  const { t } = useTranslation();
  const { mutateAsync: createImageCredentials } =
    useApiV10MediaRichTextImagesCreate();

  const uploadImage = useCallback(
    async (file: File): Promise<string> => {
      if (file.size > MAX_IMAGE_SIZE) {
        toast.error(
          t(
            'imageUpload.errors.fileTooLarge',
            'Image size must be less than 5MB'
          )
        );
        throw new Error('File too large');
      }

      if (!file.type.startsWith('image/')) {
        toast.error(
          t(
            'imageUpload.errors.invalidFileType',
            'File type not allowed. Supported file types are: .png/.jpg/.jpeg/.heic/.webp'
          )
        );
        throw new Error('Invalid file type');
      }

      try {
        const payload: RichTextImageUploadRequestRequest = {
          filename: generateFilename(file),
          contentType: file.type,
          fileSize: file.size,
          employerId,
        };

        const response = await createImageCredentials({ data: payload });

        await uploadToS3(file, {
          uuid: response.uuid,
          uploadUrl: response.uploadUrl,
          uploadFields: response.uploadFields as Record<string, string>,
          placeholderUrl: response.placeholderUrl,
        });

        const signedUrl = new URL(response.signedUrl);
        signedUrl.hash = `placeholder_url=${encodeURIComponent(response.placeholderUrl)}`;
        return signedUrl.toString();
      } catch (error) {
        const axiosError = error as AxiosError<Record<string, string[]>>;
        if (axiosError.response?.data) {
          const firstError = Object.values(axiosError.response.data)[0];
          if (firstError && Array.isArray(firstError)) {
            throw new Error(firstError[0]);
          }
        }
        throw error;
      }
    },
    [createImageCredentials, employerId, t]
  );

  return { uploadImage };
};
